import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentTenant } from "@/hooks/useCurrentTenant";
import { api, suiteLogoutUrl } from "@/lib/api-client";
import "./UserMenu.css";

/**
 * Followups #4 + #6 — Header user menu.
 *
 * Trigger button shows the signed-in user's name + a chevron. Clicking
 * opens a small panel with the per-user Settings link (closing #6 — the
 * /settings entry was missing from any nav) and a Logout action (closing
 * #4 — header username/logout were missing).
 *
 * Closes outside-click + Escape. Hidden until auth resolves so we don't
 * flash an empty trigger on initial paint.
 */
export function UserMenu() {
  const { user, isLoading } = useAuth();
  const { data: tenant } = useCurrentTenant();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onClickOutside);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    setOpen(false);
    try {
      await api.post("/auth/logout");
    } catch {
      // Even if the server is unreachable we still bounce the user to
      // login — destroying the local cookie alone is enough.
    }
    // End the Suite session too (F-CAPTURE-005): bouncing to "/" sent the user
    // through Suite SSO, which was still signed in and signed them straight back.
    window.location.href = suiteLogoutUrl(`${window.location.origin}/signed-out`);
  };

  if (isLoading || !user) return null;

  const initials = (user.name || user.email || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("");

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        type="button"
        className="user-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="user-menu-trigger"
      >
        <span className="user-menu__avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="user-menu__name">
          <span className="user-menu__name-line">{user.name || user.email}</span>
          <span className="user-menu__tenant-line">{tenant?.name ?? 'Tenant'}</span>
        </span>
        <svg
          className="user-menu__chevron"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open ? (
        <div className="user-menu__panel" role="menu" data-testid="user-menu-panel">
          <div className="user-menu__header">
            <div className="user-menu__header-name">{user.name || "User"}</div>
            <div className="user-menu__header-email">{user.email}</div>
          </div>
          <Link
            to="/settings"
            role="menuitem"
            className="user-menu__item"
            onClick={() => setOpen(false)}
          >
            Settings
          </Link>
          <button
            type="button"
            role="menuitem"
            className="user-menu__item user-menu__item--logout"
            onClick={handleLogout}
            data-testid="user-menu-logout"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
